import { Component, EventEmitter, Input, OnChanges, OnInit, Output, SimpleChanges, ViewEncapsulation } from '@angular/core';
import { IDashboardSection } from './dashboard.component';
import { GaDeferredService, IGADeferred } from '@app/shared/services/ga-deferred.service';
import { IReportMetadata } from '@app/shared/report/report-data.service';
import { DashboardService } from './dashboard.service';
import { IDashboardSectionValidation } from './dashboard-metadata.service';
import { IStoredReport } from '@app/shared/report/stored-report-data.service';

@Component({
  selector: 'dashboard-section',
  template: `
    <editable-text [key]="section.key" [screen]="section.screen" menu="title,info" highlight="h3 a:eq(0)"></editable-text>

    <collapsible-pane [title]="section.name | translate" [collapsed]="section.collapsed" (onChange)="toggleSection($event)" [partialRender]="section.partialRender">
      <div class="collapsible-title-extra">
        <span class="detailed-report-link">
          <a *ngIf="section.report.ref" [uiSref]="section.report.ref.state" [uiParams]="section.report.ref.params" target="_blank"><i class="fa fa-table"></i></a>
        </span>

        <span class="detailed-report-link">
          <a href="javascript:void(0)" (click)="onRefreshSection.emit(section)"><i class="fa fa-refresh"></i></a>
        </span>

        <span class="report-result-count">
          <activity-indicator *ngIf="section.report.loading || section.report.initializing"></activity-indicator>
          <span *ngIf="!section.report.loading && (section.report.indicator === 0 || section.report.indicator)" class="warn">{{ section.report.indicator }}</span>
        </span>

        <span *ngIf="section.extraTextValue" class="detailed-report-link">
          <span *ngIf="section.extraTextValue.label">{{ section.extraTextValue.label }}</span>
          <a *ngIf="section.extraTextValue.report" [uiSref]="section.extraTextValue.report.ref.state" [uiParams]="section.extraTextValue.report.ref.params" target="_blank">{{ section.extraTextValue.report.name }}</a>
        </span>

        <span class="report-info" *ngIf="section.report.infoComponent">
          <ndc-dynamic [ndcDynamicComponent]="section.report.infoComponent?.name" [ndcDynamicInputs]="getInfoComponentInputs(section)" [ndcDynamicOutputs]="section.report.infoComponent?.outputs"></ndc-dynamic>
        </span>
      </div>

      <div class="alert alert-warning" *ngIf="isSectionInvalid(section.report.validations)">
        <div><i class="fa fa-exclamation-triangle"></i> {{ getFirstInvalidMessage(section.report.validations) }}</div>
      </div>

      <!-- mocks ag-grid table for the case when metadata is not loaded yet -->
      <div *ngIf="!section.report.gridMetadata">
        <div class="report-info">
          <div class="report-comments" *ngIf="section.report.comments !== (section.report.comments | translate)" [innerHTML]="section.report.comments | translate | safe: 'html'"></div>
        </div>
        <div class="report-without-data"></div>
      </div>

      <embedded-report
        *ngIf="section.report.gridMetadata"
        [layoutGridCode]="section.report.layoutGridCode"
        [sectionId]="section.id"
        [reportId]="section.report.id"
        [browselistConfig]="section.report.browseListConfig"
        [reportComments]="section.report.comments"
        [reportMetadata]="section.report.gridMetadata"
        [(reportDataLoading)]="section.report.loading"
        [reportData]="section.report.data"
        [reportSort]="section.report.sort"
        [subColumns]="section.report.subColumns"
        [(queryFilters)]="section.report.queryFilters"
        [gridHeight]="section.report.gridHeight"
        [storedReportData]="storedReportData"
        [layoutSetting]="section.report.layoutSetting"
        (onGridReady)="onGridReady()">
      </embedded-report>

    </collapsible-pane>
  `,
  styleUrls: ['./dashboard.scss'],
  encapsulation: ViewEncapsulation.None
})
export class DashboardSectionComponent implements OnInit, OnChanges {
  @Input() public section:IDashboardSection;
  @Input() public screen:string;
  @Input() public getInfoComponentInputs:Function;
  @Input() public reportMetadata:IReportMetadata;
  @Input() public storedReportData:IStoredReport;
  @Output() public onRefreshSection:EventEmitter<IDashboardSection> = new EventEmitter<IDashboardSection>();

  private dashboardSectionDeferred:IGADeferred<void | IDashboardSection>;
  private gridReadyDeferred:IGADeferred<void>;

  constructor(
    private dashboardService:DashboardService,
    private deferredService:GaDeferredService
  ) {
    this.gridReadyDeferred = this.deferredService.defer();
  }

  public ngOnInit():void {
    this.section.report.comments = `${this.screen}.${this.section.key}.INFO`;

    if (!this.section.report.init) {
      this.dashboardSectionDeferred = this.deferredService.defer<void | IDashboardSection>();

      this.section.report.init = this.dashboardSectionDeferred.promise;
    }
  }

  public ngOnChanges(changes:SimpleChanges):void {
    if (changes.reportMetadata?.currentValue) {
      this.reportMetadata = changes.reportMetadata.currentValue;
      this.setMetadataForSection(this.section, this.reportMetadata);
    }

    if (changes.storedReportData?.currentValue) {
      this.storedReportData = changes.storedReportData.currentValue;
    }
  }

  private setMetadataForSection(section:IDashboardSection, reportMetadata:IReportMetadata):void {
    const reportId:number = section.report.browseListConfig? section.report.browseListConfig.reportId : section.report.id;

    section.report.initializing = true;

    if (reportId < 0) { //report not done yet
      this.dashboardSectionDeferred.resolve();
      section.report.initializing = false;
    }

    if(reportMetadata){
      section.report.metadata = reportMetadata;
      section.report.gridMetadata = this.dashboardService.filterVisibleColumns(section, reportMetadata);
      this.dashboardService.createReportRefs(section);
      this.dashboardSectionDeferred.resolve();
    } else {
      // resolve promise with the failed section so that the report won't be considered when loading the data
      this.dashboardSectionDeferred.resolve(section);
    }

    section.report.initializing = false;
  }

  public isSectionInvalid(validations:IDashboardSectionValidation[]):boolean {
    return this.dashboardService.getInvalidValidations(validations).length > 0;
  }

  public getFirstInvalidMessage(validations:IDashboardSectionValidation[]):string {
    // we will only get the first invalid message
    return this.dashboardService.getInvalidValidations(validations)?.[0]?.message;
  }

  public toggleSection(collapsed:boolean):void {
    this.dashboardService.toggleSection(this.section, collapsed);
  }

  public onGridReady():void {
    this.gridReadyDeferred.resolve();
  }
}